// @ts-nocheck
import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Loader2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  Sparkles,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { useStore } from "@tanstack/react-form";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/shared/NumericInput";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  mergeUniqueRoutingSteps,
  RoutingStepRow,
} from "./routing-dedup.helper";

interface SortableRoutingRowProps {
  id: string;
  index: number;
  row: any;
  totalRows: number;
  onUpdate: (updates: any) => void;
  onRemove: () => void;
  onMove: (fromIndex: number, toIndex: number) => void;
}

function SortableRoutingRow({
  id,
  index,
  row,
  totalRows,
  onUpdate,
  onRemove,
  onMove,
}: SortableRoutingRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-slate-50/80 transition-colors border-b border-slate-100 group ${
        isDragging ? "bg-indigo-50 shadow-md ring-1 ring-indigo-300" : ""
      }`}
    >
      {/* Cột Kéo Thả (Drag Handle) & Nút Lên/Xuống */}
      <td className="px-1.5 py-1.5 align-middle text-center w-16">
        <div className="flex items-center justify-center gap-0.5">
          <button
            type="button"
            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 cursor-grab active:cursor-grabbing touch-none"
            title="Kéo để thay đổi thứ tự công đoạn"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex flex-col">
            <button
              type="button"
              disabled={index === 0}
              onClick={() => onMove(index, index - 1)}
              className="p-0.5 text-slate-400 hover:text-indigo-600 disabled:opacity-20 disabled:cursor-not-allowed"
              title="Di chuyển lên"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              disabled={index === totalRows - 1}
              onClick={() => onMove(index, index + 1)}
              className="p-0.5 text-slate-400 hover:text-indigo-600 disabled:opacity-20 disabled:cursor-not-allowed"
              title="Di chuyển xuống"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
        </div>
      </td>

      {/* STT */}
      <td className="px-2 py-1.5 text-center text-slate-500 font-mono text-xs w-10 font-bold">
        {index + 1}
      </td>

      {/* Mã công đoạn */}
      <td className="px-1 py-1 w-[160px]">
        <div className="h-8">
          <SharedCombobox
            type="process"
            value={row.stepId || ""}
            valueLabel={row.name || row.stepName || row.stepId}
            onChange={(id, opt) => {
              const orig = opt?.original || {};
              const stepCode = orig.stepId || id;
              const procName = orig.name || opt?.label || row.name || "";
              const setupTime = orig.defaultSetupTime ?? orig.productionTime ?? 30;
              const runSpeed = orig.defaultSpeed ?? orig.speed ?? 5000;
              const setupWaste = orig.defaultSetupWaste ?? orig.minWasteQty ?? 30;
              const runWastePct = Number(orig.defaultRunWastePct ?? orig.wastePercent ?? 2.0);

              onUpdate({
                ...row,
                stepId: stepCode,
                name: !row.name ? procName : row.name,
                plannedSetupTime:
                  !row.plannedSetupTime || row.plannedSetupTime === 0
                    ? setupTime
                    : row.plannedSetupTime,
                plannedSpeed:
                  !row.plannedSpeed || row.plannedSpeed === 0
                    ? runSpeed
                    : row.plannedSpeed,
                plannedSetupWaste:
                  !row.plannedSetupWaste || row.plannedSetupWaste === 0
                    ? setupWaste
                    : row.plannedSetupWaste,
                plannedRunWastePct:
                  !row.plannedRunWastePct || row.plannedRunWastePct === 0
                    ? runWastePct
                    : row.plannedRunWastePct,
              });
            }}
            placeholder="Chọn công đoạn..."
          />
        </div>
      </td>

      {/* Tên công đoạn */}
      <td className="px-1 py-1 min-w-[150px]">
        <Input
          className="h-8 text-xs font-medium"
          value={row.name || ""}
          placeholder="Tên công đoạn..."
          onChange={(e) => onUpdate({ ...row, name: e.target.value })}
        />
      </td>

      {/* TG Cài đặt (p) */}
      <td className="px-1 py-1 w-[90px]">
        <NumericInput
          value={Number(row.plannedSetupTime) || 0}
          onChange={(val) => onUpdate({ ...row, plannedSetupTime: val })}
          decimals={0}
          placeholder="30"
          className="h-8 text-xs text-right"
        />
      </td>

      {/* Tốc độ (sp/h) */}
      <td className="px-1 py-1 w-[95px]">
        <NumericInput
          value={Number(row.plannedSpeed) || 0}
          onChange={(val) => onUpdate({ ...row, plannedSpeed: val })}
          decimals={0}
          placeholder="5000"
          className="h-8 text-xs text-right"
        />
      </td>

      {/* Hao phí CĐ */}
      <td className="px-1 py-1 w-[85px]">
        <NumericInput
          value={Number(row.plannedSetupWaste) || 0}
          onChange={(val) => onUpdate({ ...row, plannedSetupWaste: val })}
          decimals={0}
          placeholder="30"
          className="h-8 text-xs text-right"
        />
      </td>

      {/* Hao phí chạy (%) */}
      <td className="px-1 py-1 w-[85px]">
        <NumericInput
          value={Number(row.plannedRunWastePct) || 0}
          onChange={(val) => onUpdate({ ...row, plannedRunWastePct: val })}
          decimals={2}
          placeholder="2.0"
          className="h-8 text-xs text-right text-rose-600 font-semibold"
        />
      </td>

      {/* TG Ước lượng (h) */}
      <td className="px-1 py-1 w-[90px]">
        <NumericInput
          value={Number(row.estDurationHours) || 0}
          onChange={(val) => onUpdate({ ...row, estDurationHours: val })}
          decimals={1}
          placeholder="1.5"
          className="h-8 text-xs text-right"
        />
      </td>

      {/* Đơn vị (tờ / pcs) */}
      <td className="px-1 py-1 w-[70px]">
        <select
          value={row.productionUnit || (
            (row.name || '').toLowerCase().includes('dán') || 
            (row.name || '').toLowerCase().includes('gói') || 
            (row.name || '').toLowerCase().includes('qc') ? 'PCS' : 'SHEET'
          )}
          onChange={(e) => onUpdate({ ...row, productionUnit: e.target.value })}
          className="h-8 w-full text-xs font-semibold rounded px-1 border border-slate-200 bg-white text-slate-700 cursor-pointer focus:outline-none"
        >
          <option value="SHEET">tờ</option>
          <option value="PCS">pcs</option>
        </select>
      </td>

      {/* SL Kế hoạch */}
      <td className="px-1 py-1 w-[115px] min-w-[115px]">
        <NumericInput
          value={
            Number(
              row.expectedOutput ??
                row.standardQuantity ??
                row.plannedQty ??
                row.inputQty
            ) || 0
          }
          onChange={(val) =>
            onUpdate({
              ...row,
              expectedOutput: val,
              standardQuantity: val,
              plannedQty: val,
            })
          }
          decimals={0}
          placeholder="0"
          className="h-8 text-xs text-right font-mono font-bold"
        />
      </td>

      {/* Yêu cầu kỹ thuật của công đoạn (dán, xé, cắt thành phẩm, tráng phủ, chiết quang, bồi...) */}
      <td className="px-1 py-1 min-w-[220px]">
        <Input
          data-testid="routing-step-tech-req"
          className="h-8 text-xs"
          value={row.technicalRequirements || ""}
          title={row.technicalRequirements || ""}
          placeholder="VD: dán mộc 1 điểm, xé răng cưa, cắt xẻ 4 biên..."
          onChange={(e) => onUpdate({ ...row, technicalRequirements: e.target.value })}
        />
      </td>

      {/* Nút Xóa */}
      <td className="px-1 py-1 text-center w-9">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 transition"
          onClick={onRemove}
          title="Xóa công đoạn"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

export function RoutingBuilder({
  form,
  componentIndex,
  productionOrderId,
}: {
  form: any;
  componentIndex: number;
  productionOrderId?: string;
}) {
  const selectedProductId = useStore(
    form.store,
    (s: any) => s.values.productId
  );

  const orderSources = useStore(
    form.store,
    (s: any) => s.values.orderSources
  ) || [];

  const routingSteps: any[] =
    useStore(
      form.store,
      (s: any) => s.values.components?.[componentIndex]?.routingSteps
    ) || [];

  const { data: fullProduct, isLoading } = useQuery({
    queryKey: ["product-details", selectedProductId],
    queryFn: () =>
      selectedProductId
        ? trpcClient.products.getById.query({ id: selectedProductId })
        : null,
    enabled: !!selectedProductId,
  });

  // Cấu hình Sensor cho dnd-kit (bắt buộc kéo ít nhất 5px để không nuốt click vào input/combobox)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const syncRoutingStepsToForm = useCallback(
    (newSteps: any[], isUserAction = true) => {
      // Đảm bảo stepOrder tuần tự 1..N
      const reIndexed = newSteps.map((step, idx) => ({
        ...step,
        stepOrder: idx + 1,
        isUserCustomized: isUserAction ? true : step.isUserCustomized,
      }));

      const currentComponents = form.getFieldValue("components") || [];
      const newComponents = [...currentComponents];
      const existingComp = newComponents[componentIndex] || {};
      newComponents[componentIndex] = {
        ...existingComp,
        // Giữ tên và vai trò hợp phần đã có (trước đây bị ghi đè thành "Main"/"Component N")
        isMain: existingComp.isMain ?? componentIndex === 0,
        componentName:
          existingComp.componentName || (componentIndex === 0 ? "Main" : `Hợp phần ${componentIndex + 1}`),
        routingSteps: reIndexed,
        isRoutingUserCustomized: isUserAction ? true : existingComp.isRoutingUserCustomized,
      };
      form.setFieldValue("components", newComponents);

      if (productionOrderId) {
        toast.info("Đã cập nhật quy trình công đoạn", { duration: 1000 });
      }
    },
    [form, componentIndex, productionOrderId]
  );

  // Xử lý sự kiện kéo thả hoàn tất
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = routingSteps.findIndex(
      (s, idx) => (s.id || `step-${idx}`) === active.id
    );
    const newIndex = routingSteps.findIndex(
      (s, idx) => (s.id || `step-${idx}`) === over.id
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(routingSteps, oldIndex, newIndex);
      syncRoutingStepsToForm(reordered);
      toast.success(`Đã đổi vị trí công đoạn sang bước #${newIndex + 1}`, { duration: 1500 });
    }
  };

  // Di chuyển dòng bằng nút mũi tên Lên/Xuống
  const handleMoveRow = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= routingSteps.length) return;
    const reordered = arrayMove(routingSteps, fromIndex, toIndex);
    syncRoutingStepsToForm(reordered);
  };

  // Cập nhật 1 dòng công đoạn
  const handleUpdateRow = (index: number, updates: any) => {
    const updated = routingSteps.map((r, i) => (i === index ? updates : r));
    syncRoutingStepsToForm(updated);
  };

  // Xóa 1 dòng công đoạn
  const handleRemoveRow = (index: number) => {
    const updated = routingSteps.filter((_, i) => i !== index);
    syncRoutingStepsToForm(updated);
    toast.info("Đã xóa công đoạn khỏi kế hoạch");
  };

  // Thêm 1 dòng trống thủ công
  const handleAddEmptyRow = () => {
    const newRow = {
      id: `custom-step-${Date.now()}`,
      stepId: "",
      name: "",
      productionUnit: "SHEET",
      plannedSetupTime: 30,
      plannedSpeed: 5000,
      plannedSetupWaste: 30,
      plannedRunWastePct: 2.0,
      estDurationHours: 1.0,
      expectedOutput: 0,
      status: "PLANNED",
      stepOrder: routingSteps.length + 1,
    };
    syncRoutingStepsToForm([...routingSteps, newRow]);
  };

  // Tự động tải từ sản phẩm & Khử trùng lặp (Deduplication)
  const handleAutoFillWithDedup = () => {
    if (!fullProduct?.productProcesses) return;

    // Sử dụng helper mergeUniqueRoutingSteps để tự động khử trùng lặp.
    // Lệnh ghép: nạp hợp quy trình của MỌI sản phẩm trong lệnh (không chỉ sản phẩm chính).
    const extraSources = Array.isArray(orderSources) && orderSources.length > 1 ? orderSources.slice(1) : [];
    const merged = mergeUniqueRoutingSteps(
      fullProduct.productProcesses,
      extraSources.map((src: any) => ({
        processes: src.productProcesses || [],
        productCode: src.productCode || src.productName,
        productName: src.productName,
        quantity: Number(src.plannedQty ?? src.quantity) || 0,
        orderDetailId: src.orderDetailId,
      })),
      extraSources.length ? { isMultiProduct: true, primaryProductName: fullProduct.productName, primaryProductCode: fullProduct.productCode } : undefined,
    );
    syncRoutingStepsToForm(merged);
    toast.success(`Đã đồng bộ ${merged.length} công đoạn (Tự động khử trùng lặp)`);
  };

  const isMultiSource = Array.isArray(orderSources) && orderSources.length > 1;

  // Lập danh sách id cho sortable
  const sortableItems = routingSteps.map(
    (s, idx) => s.id || `step-${idx}`
  );

  return (
    <div className="space-y-3">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            Quy trình sản xuất (Routing)
          </h4>
          <Badge variant="outline" className="text-xs font-mono font-medium bg-slate-50">
            {routingSteps.length} công đoạn
          </Badge>
          {isMultiSource && (
            <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Gộp {orderSources.length} sản phẩm
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleAutoFillWithDedup}
            disabled={!fullProduct || isLoading}
            className="text-xs h-8 text-indigo-700 border-indigo-200 bg-indigo-50/50 hover:bg-indigo-100"
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5 mr-1.5" />
            )}
            Tải &amp; Khử trùng lặp
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleAddEmptyRow}
            className="text-xs h-8 text-slate-700 bg-white hover:bg-slate-50 border-slate-300"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Thêm công đoạn
          </Button>
        </div>
      </div>

      {/* Bảng Kéo Thả Sortable Table */}
      <div className="border rounded-lg overflow-x-auto bg-white shadow-2xs">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="w-full min-w-[1200px] text-xs text-left border-collapse">
            <thead className="bg-slate-50 text-slate-600 font-semibold uppercase text-[10px] border-b border-slate-200">
              <tr>
                <th className="px-1.5 py-2.5 text-center w-16">Thứ tự</th>
                <th className="px-2 py-2.5 text-center w-10">#</th>
                <th className="px-1 py-2.5 w-[160px]">Mã công đoạn</th>
                <th className="px-1 py-2.5 min-w-[150px]">Tên công đoạn</th>
                <th className="px-1 py-2.5 text-right w-[90px]">Cài đặt (p)</th>
                <th className="px-1 py-2.5 text-right w-[95px]">Tốc độ (sp/h)</th>
                <th className="px-1 py-2.5 text-right w-[85px]">Phí CĐ</th>
                <th className="px-1 py-2.5 text-right w-[85px]">Phí chạy (%)</th>
                <th className="px-1 py-2.5 text-right w-[90px]">TG tính (h)</th>
                <th className="px-1 py-2.5 text-center w-[70px]">Đơn vị</th>
                <th className="px-1 py-2.5 text-right w-[95px]">SL KH</th>
                <th className="px-1 py-2.5 min-w-[220px]">Yêu cầu kỹ thuật</th>
                <th className="px-1 py-2.5 w-9"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {routingSteps.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-slate-400 italic">
                    Chưa có công đoạn nào. Nhấn "Tải &amp; Khử trùng lặp" hoặc "Thêm công đoạn" để thiết lập.
                  </td>
                </tr>
              ) : (
                <SortableContext
                  items={sortableItems}
                  strategy={verticalListSortingStrategy}
                >
                  {routingSteps.map((row, idx) => (
                    <SortableRoutingRow
                      key={row.id || `step-${idx}`}
                      id={row.id || `step-${idx}`}
                      index={idx}
                      row={row}
                      totalRows={routingSteps.length}
                      onUpdate={(updates) => handleUpdateRow(idx, updates)}
                      onRemove={() => handleRemoveRow(idx)}
                      onMove={handleMoveRow}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </table>
        </DndContext>
      </div>
      <p className="text-[11px] text-slate-400 italic">
        * Kéo biểu tượng tay nắm ⠿ hoặc bấm mũi tên ▲/▼ để sắp xếp trình tự thi công. Hệ thống tự động lưu `stepOrder` chuẩn.
      </p>
    </div>
  );
}
